/**
 * Chat state for one conversation.
 *
 * Owns the turn lifecycle: send a question, consume the SSE stream, accumulate the streaming
 * answer, attach citations when they arrive, and end in one of four terminal states — answered,
 * not-in-knowledge-base, refused, or error.
 *
 * The four are kept distinct rather than collapsed into "assistant said something". A refusal that
 * renders like a weak answer teaches employees that the assistant is unreliable; a refusal that
 * reads as deliberate teaches them when to trust it. That distinction is a product requirement, so
 * it lives in the state model rather than in the styling.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import {
  type Citation,
  ChatStreamError,
  type FeatureOffer,
  type Language,
  streamChat,
  useLanguage,
} from "@nab/shared";

export type MessageOutcome =
  | "answered"
  | "no_grounding"
  | "refused"
  | "withheld"
  | "error";

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  /** Answer text, growing while the turn streams. */
  content: string;
  language: Language;
  outcome: MessageOutcome | null;
  citations: Citation[];
  /** The compliance-authority disclaimer for a regulatory answer (UC-A3), when the server sent
   *  one. Held on the message rather than derived from the citations: the wording is the bank's,
   *  and reconstructing it in the client would be the client authoring a legal notice. */
  regulatoryDisclaimer: string | null;
  /** Department to contact, on the not-in-knowledge-base path. */
  suggestedDepartment: string | null;
  /** True while this message is still receiving tokens. */
  streaming: boolean;
  /** The turn was not recorded, so it did not happen. Shown with its own wording. */
  auditFailure: boolean;
  /**
   * A feature the assistant is offering to start (UC-B4), or null.
   *
   * Held beside the answer rather than replacing it. ADR 0007: the assistant offers and does not
   * switch modes, so an employee who asked a question gets the answer whether or not the matcher
   * also thought they were asking for something.
   */
  featureOffer: FeatureOffer | null;
}

export interface UseChatResult {
  messages: ChatMessage[];
  sessionId: string | null;
  sending: boolean;
  send: (question: string) => Promise<void>;
  stop: () => void;
  reset: () => void;
  /** Sets the conversation to a previously stored one, for the history view. */
  load: (sessionId: string, messages: ChatMessage[]) => void;
}

let localId = 0;
const nextId = (prefix: string): string => `${prefix}-${(localId += 1)}`;

export function useChat(options: { employeeId?: string } = {}): UseChatResult {
  const { language } = useLanguage();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  // Abort in flight work on unmount, so a stream does not keep writing into a dead component.
  useEffect(() => () => abortRef.current?.abort(), []);

  const patchAssistant = useCallback(
    (id: string, patch: Partial<ChatMessage>) => {
      setMessages((current) =>
        current.map((message) => (message.id === id ? { ...message, ...patch } : message)),
      );
    },
    [],
  );

  const send = useCallback(
    async (question: string) => {
      const trimmed = question.trim();
      if (!trimmed || sending) return;

      const controller = new AbortController();
      abortRef.current = controller;
      setSending(true);

      const userMessage: ChatMessage = {
        id: nextId("user"),
        role: "user",
        content: trimmed,
        language: "en",
        outcome: null,
        citations: [],
        regulatoryDisclaimer: null,
        suggestedDepartment: null,
        streaming: false,
        auditFailure: false,
        featureOffer: null,
      };
      const assistantId = nextId("assistant");
      const placeholder: ChatMessage = {
        ...userMessage,
        id: assistantId,
        role: "assistant",
        content: "",
        streaming: true,
      };

      setMessages((current) => [...current, userMessage, placeholder]);

      try {
        // Accumulated locally and flushed into state per event. Appending to state on every
        // token would re-render the whole list for each delta.
        let answer = "";

        // Built rather than inlined: under exactOptionalPropertyTypes, passing an explicit
        // `employeeId: undefined` is not the same as omitting it.
        const streamOptions = options.employeeId
          ? { signal: controller.signal, employeeId: options.employeeId }
          : { signal: controller.signal };

        for await (const event of streamChat(
          // The interface language goes with every turn. It is not an override — the server
          // answers a plainly Arabic question in Arabic whatever the interface says — it is the
          // tiebreaker for everything a single message cannot settle: a product name, an account
          // number, "ok", a transliterated question. An employee who has set the whole application
          // to Arabic has already said which language they work in. See `resolve_language`.
          { message: trimmed, session_id: sessionId, language },
          streamOptions,
        )) {
          switch (event.event) {
            case "start":
              setSessionId(event.data.session_id);
              // The server decides the answer language; the UI follows it rather than guessing,
              // so a dialect question answered in Arabic renders right-to-left correctly.
              patchAssistant(assistantId, { language: event.data.language });
              break;

            case "token":
              answer += event.data.delta;
              patchAssistant(assistantId, { content: answer });
              break;

            case "citations":
              patchAssistant(assistantId, {
                citations: event.data.citations,
                regulatoryDisclaimer: event.data.regulatory_disclaimer,
              });
              break;

            case "no_grounding":
              patchAssistant(assistantId, {
                content: event.data.message,
                outcome: "no_grounding",
                suggestedDepartment: event.data.suggested_department,
              });
              break;

            case "refusal":
              patchAssistant(assistantId, {
                content: event.data.message,
                outcome: "refused",
              });
              break;

            case "feature_offer":
              patchAssistant(assistantId, { featureOffer: event.data.feature });
              break;

            case "withheld":
              // The answer failed the output checks after it was already on screen (ADR 0003).
              // Discarding `answer` is the whole point of this event: SSE cannot un-send a token,
              // so retraction is the client's job, and leaving the text up would defeat the check.
              answer = "";
              if (event.data.message === null) {
                // A replacement is being generated and will arrive as further tokens. The text is
                // cleared and the message stays in its streaming state rather than flashing a
                // refusal the employee would then watch being overwritten.
                patchAssistant(assistantId, {
                  content: "",
                  citations: [],
                  regulatoryDisclaimer: null,
                });
              } else {
                patchAssistant(assistantId, {
                  content: event.data.message,
                  citations: [],
        regulatoryDisclaimer: null,
                  outcome: "withheld",
                });
              }
              break;

            case "error":
              patchAssistant(assistantId, {
                content: event.data.message,
                outcome: "error",
                auditFailure: event.data.audit_failure,
                streaming: false,
              });
              break;

            case "done": {
              // `done` arrives after the outcome events, so an otherwise-unmarked turn is an
              // answer. The id is swapped to the server's message id here and not earlier: it is
              // what feedback attaches to (UC-A5), and it only exists once the turn is recorded.
              const messageId = event.data.message_id;
              setMessages((current) =>
                current.map((message) =>
                  message.id === assistantId
                    ? {
                        ...message,
                        id: messageId ?? assistantId,
                        streaming: false,
                        outcome: message.outcome ?? "answered",
                      }
                    : message,
                ),
              );
              break;
            }
          }
        }
      } catch (error) {
        if (controller.signal.aborted) {
          patchAssistant(assistantId, { streaming: false });
        } else {
          const isAuditFailure =
            error instanceof ChatStreamError && error.status === 503;
          patchAssistant(assistantId, {
            content: error instanceof Error ? error.message : String(error),
            outcome: "error",
            auditFailure: isAuditFailure,
            streaming: false,
          });
        }
      } finally {
        setSending(false);
        abortRef.current = null;
      }
    },
    [options.employeeId, patchAssistant, sending, sessionId],
  );

  const stop = useCallback(() => abortRef.current?.abort(), []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    setMessages([]);
    setSessionId(null);
  }, []);

  const load = useCallback((id: string, stored: ChatMessage[]) => {
    abortRef.current?.abort();
    setSessionId(id);
    setMessages(stored);
  }, []);

  return { messages, sessionId, sending, send, stop, reset, load };
}
